import documentMappings from '../config/documentMappings.json';

/**
 * Gets the download URL for a document based on its name
 * @param documentName - The name of the document (without file extension)
 * @returns The Highspot URL for the document, or null if not found
 */
export function getDocumentUrl(documentName: string): string | null {
  // Clean the document name by removing file extensions
  const cleanName = documentName.replace(/\.(docx?|pdf|txt)$/i, '');

  // Check direct mapping first
  if (documentMappings[cleanName as keyof typeof documentMappings]) {
    return documentMappings[cleanName as keyof typeof documentMappings];
  }

  // Check for numbered documents pattern (GUIDE001, EventSurveys001, etc.)
  if (/\d{3}/.test(cleanName)) {
    return documentMappings._defaultNumberedDocUrl;
  }

  // Try partial matching for similar names
  const mappingKeys = Object.keys(documentMappings).filter((key) => !key.startsWith('_'));
  const partialMatch = mappingKeys.find(
    (key) => key.toLowerCase().includes(cleanName.toLowerCase()) || cleanName.toLowerCase().includes(key.toLowerCase()),
  );

  if (partialMatch) {
    return documentMappings[partialMatch as keyof typeof documentMappings];
  }

  return null;
}

/**
 * Opens a document in a new tab
 * @param documentName - The name of the document to open
 */
export function openDocument(documentName: string): void {
  const url = getDocumentUrl(documentName);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    console.warn(`No URL mapping found for document: ${documentName}`);
  }
}

/**
 * Checks if a document has a download URL available
 * @param documentName - The name of the document to check
 * @returns True if the document has a URL mapping
 */
export function hasDocumentUrl(documentName: string): boolean {
  return getDocumentUrl(documentName) !== null;
}
